/**
 * Navigation service for generating and handling URLs
 * This service provides utilities for working with application routes and URL parameters
 */

/**
 * Validates site parameter
 * @param site Site identifier
 * @returns True if the site parameter is valid, false otherwise
 */
export const isValidSite = (site: string | undefined): boolean => {
  // Basic validation - site should be a non-empty string
  // In a real application, this would validate against a list of valid sites
  return Boolean(site && site.trim().length > 0);
};

/**
 * Validates APM ID parameter
 * @param apmId APM identifier
 * @returns True if the APM ID parameter is valid, false otherwise
 */
export const isValidApmId = (apmId: string | undefined): boolean => {
  // Basic validation - APM ID should be a non-empty string
  // In a real application, this would validate against a specific format or pattern
  return Boolean(apmId && apmId.trim().length > 0);
};

/**
 * Validates both site and APM ID parameters
 * @param site Site identifier
 * @param apmId APM identifier
 * @returns Object containing validation result and error message if invalid
 */
export const validateEquipmentParams = (site: string | undefined, apmId: string | undefined): { 
  isValid: boolean; 
  errorMessage?: string;
} => {
  if (!isValidSite(site)) {
    return { 
      isValid: false, 
      errorMessage: 'Invalid site parameter. ' 
    };
  }
  
  if (!isValidApmId(apmId)) {
    return { 
      isValid: false, 
      errorMessage: 'Invalid asset parameter. ' 
    };
  }
  
  return { isValid: true };
};

/**
 * Generates a URL for the equipment status overview page
 * @param site Site identifier
 * @param apmId APM identifier
 * @returns URL string for the equipment status overview page
 */
export const getEquipmentStatusUrl = (site: string, apmId: string): string => {
  return `/equipment-status?site=${encodeURIComponent(site)}&asset=${encodeURIComponent(apmId)}`;
};

/**
 * Generates a URL for the equipment status overview page with a specific node selected
 * @param site Site identifier
 * @param apmId APM identifier
 * @param nodeId Selected node identifier
 * @returns URL string for the equipment status overview page with node selection
 */
export const getEquipmentNodeUrl = (site: string, apmId: string, nodeId: string): string => {
  const baseUrl = getEquipmentStatusUrl(site, apmId);
  return `${baseUrl}&nodeId=${encodeURIComponent(nodeId)}`;
};

/**
 * Extracts query parameters from the current URL
 * @param search URL search string (e.g., "?nodeId=123")
 * @returns Object containing parsed query parameters
 */
export const parseQueryParams = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  
  params.forEach((value, key) => {
    result[key] = value;
  });
  
  return result;
};